import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MapPin, UserCog, ExternalLink, ShieldCheck, Loader2, Trash2, Camera, Package, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import OrderPhotosUploader from "@/components/OrderPhotosUploader";

const STATUSES = ["ordered", "enroute", "on_site", "in_progress", "completed"];
const STATUS_LABEL = { ordered: "Ordered", enroute: "En route", on_site: "On site", in_progress: "In progress", completed: "Completed" };
const fmt = (c) => `$${(c / 100).toFixed(2)}`;

function timeAgo(iso) {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.round(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function MiniMap({ order }) {
  const loc = order.tech_location;
  if (!loc) {
    return order.shipping_address ? (
      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.shipping_address)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-cyan-700 hover:underline" data-testid={`order-map-link-${order.id}`}>
        <MapPin className="h-3 w-3" /> Site address
      </a>
    ) : <span className="text-xs text-slate-400">—</span>;
  }
  const q = `${loc.lat},${loc.lng}`;
  return (
    <div className="space-y-1">
      <div className="overflow-hidden rounded-md border border-slate-200">
        <iframe
          title={`Tech location for ${order.id}`}
          src={`https://maps.google.com/maps?q=${encodeURIComponent(q)}&z=15&output=embed`}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="block h-32 w-56"
        />
      </div>
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-slate-500">Tech: {timeAgo(loc.updated_at)}</span>
        <a href={`https://www.google.com/maps/search/?api=1&query=${q}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-cyan-700 hover:underline" data-testid={`order-tech-map-link-${order.id}`}>
          <ExternalLink className="h-3 w-3" /> open
        </a>
      </div>
    </div>
  );
}

export default function OrdersList() {
  const { user: me } = useAuth();
  const [orders, setOrders] = useState([]);
  const [techs, setTechs] = useState([]);
  const [tab, setTab] = useState("all");
  const [confirmVerify, setConfirmVerify] = useState(null); // order pending verify
  const [verifying, setVerifying] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [photosOrder, setPhotosOrder] = useState(null); // order whose photos dialog is open
  const [partsOrder, setPartsOrder] = useState(null); // order whose parts dialog is open
  const [products, setProducts] = useState([]);
  const [partsForm, setPartsForm] = useState({ product_id: "", quantity: 1 });
  const [addingPart, setAddingPart] = useState(false);
  const [removingLineId, setRemovingLineId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [o, u] = await Promise.all([api.get("/orders"), api.get("/users").catch(() => ({ data: [] }))]);
      setOrders(o.data);
      setTechs(u.data.filter((x) => ["technician", "admin", "owner"].includes(x.role)));
    } catch { toast.error("Failed to load orders"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (partsOrder && products.length === 0) {
      api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
    }
  }, [partsOrder, products.length]);

  const openPartsDialog = (order) => {
    setPartsOrder(order);
    setPartsForm({ product_id: "", quantity: 1 });
  };

  const addPart = async () => {
    if (!partsForm.product_id || partsForm.quantity < 1) {
      toast.error("Pick a product and a positive quantity");
      return;
    }
    setAddingPart(true);
    try {
      const r = await api.post(`/orders/${partsOrder.id}/items`, {
        product_id: partsForm.product_id,
        quantity: Number(partsForm.quantity),
      });
      const updated = {
        ...partsOrder,
        items: [...(partsOrder.items || []), r.data.line_item],
        total_cents: r.data.new_total_cents,
      };
      setOrders((arr) => arr.map((o) => (o.id === partsOrder.id ? updated : o)));
      setPartsOrder(updated);
      setProducts((ps) => ps.map((p) => (p.id === partsForm.product_id ? { ...p, stock: r.data.product_stock } : p)));
      setPartsForm({ product_id: "", quantity: 1 });
      toast.success(`${r.data.line_item.name} added — stock now ${r.data.product_stock}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    } finally {
      setAddingPart(false);
    }
  };

  const removePart = async (line) => {
    setRemovingLineId(line.line_id);
    try {
      const r = await api.delete(`/orders/${partsOrder.id}/items/${line.line_id}`);
      const nextItems = (partsOrder.items || []).filter((it) => it.line_id !== line.line_id);
      const updated = { ...partsOrder, items: nextItems, total_cents: r.data.new_total_cents };
      setOrders((arr) => arr.map((o) => (o.id === partsOrder.id ? updated : o)));
      setPartsOrder(updated);
      if (r.data.product_stock != null && line.product_id) {
        setProducts((ps) => ps.map((p) => (p.id === line.product_id ? { ...p, stock: r.data.product_stock } : p)));
      }
      toast.success("Line removed · stock credited");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
    } finally {
      setRemovingLineId(null);
    }
  };

  const counts = useMemo(() => {
    const c = { all: orders.length, pending_verification: orders.filter((o) => o.status === "pending_verification").length };
    STATUSES.forEach((s) => { c[s] = orders.filter((o) => o.status === s).length; });
    return c;
  }, [orders]);

  const filtered = useMemo(
    () => (tab === "all" ? orders : orders.filter((o) => o.status === tab)),
    [orders, tab]
  );

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      setOrders((arr) => arr.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success("Status updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const assign = async (id, userId) => {
    try {
      const r = await api.patch(`/orders/${id}/assign`, { user_id: userId || null });
      setOrders((arr) => arr.map((o) => (o.id === id ? r.data : o)));
      toast.success(userId ? "Technician assigned" : "Unassigned");
    } catch (e) { toast.error(e.response?.data?.detail || "Assignment failed"); }
  };

  const onConfirmVerify = async () => {
    if (!confirmVerify) return;
    setVerifying(true);
    try {
      const r = await api.post(`/orders/${confirmVerify.id}/verify-payment`);
      setOrders((arr) => arr.map((o) => (o.id === confirmVerify.id ? { ...o, status: "paid", stock_decremented: true } : o)));
      const changes = r.data?.stock_changes || [];
      toast.success(changes.length ? `Marked paid — adjusted stock for ${changes.length} item${changes.length === 1 ? "" : "s"}` : "Marked paid");
      setConfirmVerify(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const isOwnerOrAdmin = me?.role === "owner" || me?.role === "admin";

  const onConfirmDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/orders/${confirmDelete.id}`);
      setOrders((arr) => arr.filter((o) => o.id !== confirmDelete.id));
      toast.success(`Removed order from ${confirmDelete.customer_name}`);
      setConfirmDelete(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Orders & Jobs</h1>
          <p className="mt-1 text-sm text-slate-500">{orders.length} total — newest first</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex flex-wrap gap-2">
        {[
          { key: "all", label: "All" },
          { key: "pending_verification", label: "Pending verification" },
          ...STATUSES.map((s) => ({ key: s, label: STATUS_LABEL[s] })),
        ].map((t) => {
          const active = tab === t.key;
          const isPending = t.key === "pending_verification";
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium transition ${active ? (isPending ? "border-amber-600 bg-amber-600 text-white" : "border-blue-600 bg-blue-600 text-white") : (isPending && counts[t.key] ? "border-amber-300 bg-amber-50 text-amber-800 hover:border-amber-500" : "border-slate-200 bg-white text-slate-700 hover:border-blue-300")}`}
              data-testid={`orders-tab-${t.key}`}
            >
              {t.label}
              <span className={`rounded-full px-1.5 text-[11px] font-semibold ${active ? "bg-white/20" : isPending && counts[t.key] ? "bg-amber-200 text-amber-900" : "bg-slate-100 text-slate-600"}`}>{counts[t.key] || 0}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-6 dash-surface overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Items</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Assigned</th>
              <th className="px-4 py-3">GPS / Site</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Photos</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {filtered.map((o) => (
              <tr key={o.id} className="align-top" data-testid={`order-row-${o.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{o.customer_name}</div>
                  <div className="text-xs text-slate-500">{o.customer_email}</div>
                  {o.kind === "invoice" && <span className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-800">Invoice</span>}
                </td>
                <td className="px-4 py-3 text-sm text-slate-700">{o.items?.length || 0} items</td>
                <td className="px-4 py-3 font-medium text-slate-900">{fmt(o.total_cents)}</td>
                <td className="px-4 py-3">
                  {isOwnerOrAdmin ? (
                    <Select value={o.assigned_user_id || "unassigned"} onValueChange={(v) => assign(o.id, v === "unassigned" ? null : v)}>
                      <SelectTrigger className="w-44" data-testid={`order-assign-select-${o.id}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {techs.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email} <span className="text-[10px] uppercase text-slate-400 ml-1">({u.role})</span></SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex items-center gap-1 text-sm text-slate-700"><UserCog className="h-3.5 w-3.5 text-slate-400" /> {o.assigned_user_name || "—"}</div>
                  )}
                </td>
                <td className="px-4 py-3"><MiniMap order={o} /></td>
                <td className="px-4 py-3">
                  {o.status === "pending_verification" ? (
                    <div className="space-y-1">
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-800">Pending verification</span>
                      {isOwnerOrAdmin && (
                        <Button
                          size="sm"
                          onClick={() => setConfirmVerify(o)}
                          className="mt-1 w-full gap-1 bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-500"
                          data-testid={`order-verify-button-${o.id}`}
                        >
                          <ShieldCheck className="h-3.5 w-3.5" /> Mark verified
                        </Button>
                      )}
                    </div>
                  ) : o.status === "paid" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-800">Paid</span>
                  ) : o.status === "received" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-cyan-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-cyan-800">Received</span>
                  ) : (
                    <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                      <SelectTrigger className="w-40" data-testid={`order-status-select-${o.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => setPhotosOrder(o)}
                      className="inline-flex items-center gap-1 rounded-full border border-cyan-200 bg-white px-3 py-1 text-[11px] font-medium text-cyan-800 hover:bg-cyan-50"
                      data-testid={`order-photos-button-${o.id}`}
                    >
                      <Camera className="h-3 w-3" />
                      {(o.completion_photos || []).length || "Add"}
                    </button>
                    {isOwnerOrAdmin && o.status !== "paid" && o.status !== "fulfilled" && (
                      <button
                        type="button"
                        onClick={() => openPartsDialog(o)}
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white px-3 py-1 text-[11px] font-medium text-emerald-800 hover:bg-emerald-50"
                        data-testid={`order-parts-button-${o.id}`}
                        title="Add inventory parts used on this job"
                      >
                        <Package className="h-3 w-3" />
                        Parts {(o.items || []).filter((it) => it.product_id).length ? `(${(o.items || []).filter((it) => it.product_id).length})` : ""}
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(o.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">
                  {isOwnerOrAdmin && o.status !== "paid" && o.status !== "fulfilled" && !o.stock_decremented ? (
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(o)}
                      title="Remove this unpaid order"
                      className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 transition-colors hover:bg-rose-50"
                      data-testid={`order-delete-button-${o.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  ) : (
                    <span className="text-[11px] text-slate-400">—</span>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="py-12 text-center text-sm text-slate-500">No orders in this status.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!confirmVerify} onOpenChange={(open) => { if (!open && !verifying) setConfirmVerify(null); }}>
        <AlertDialogContent data-testid="order-verify-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark this payment verified?</AlertDialogTitle>
            <AlertDialogDescription>
              You're confirming that <strong className="text-slate-900">{confirmVerify?.customer_name}</strong> sent
              <strong className="text-slate-900"> {confirmVerify ? fmt(confirmVerify.total_cents) : ""}</strong> via
              <strong className="text-slate-900"> {confirmVerify?.payment_provider || "manual provider"}</strong>.
              The order will move to <strong>Paid</strong> and stock for any line items will be auto-decremented. This can't be undone from the dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={verifying} data-testid="order-verify-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); onConfirmVerify(); }}
              disabled={verifying}
              className="bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-500"
              data-testid="order-verify-confirm-button"
            >
              {verifying ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying…</> : <><ShieldCheck className="mr-2 h-4 w-4" /> Mark verified & decrement stock</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => { if (!open && !deleting) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="order-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this unpaid order?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the order from <strong className="text-slate-900">{confirmDelete?.customer_name}</strong>
              {confirmDelete ? <> for <strong className="text-slate-900">{fmt(confirmDelete.total_cents)}</strong></> : null}.
              {confirmDelete?.kind === "invoice" ? <> The originating quote will be moved back to <strong>Quoted</strong> so you can re-invoice it.</> : null} This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="order-delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); onConfirmDelete(); }}
              disabled={deleting}
              className="bg-rose-600 text-white hover:bg-rose-700 focus-visible:ring-rose-500"
              data-testid="order-delete-confirm-button"
            >
              {deleting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Removing…</> : <><Trash2 className="mr-2 h-4 w-4" /> Remove order</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!photosOrder} onOpenChange={(open) => { if (!open) setPhotosOrder(null); }}>
        <DialogContent className="max-w-3xl" data-testid="order-photos-dialog">
          <DialogHeader>
            <DialogTitle>Completion photos · {photosOrder?.customer_name}</DialogTitle>
          </DialogHeader>
          {photosOrder && (
            <OrderPhotosUploader
              orderId={photosOrder.id}
              photos={photosOrder.completion_photos || []}
              onChange={(next) => {
                setOrders((arr) => arr.map((o) => (o.id === photosOrder.id ? { ...o, completion_photos: next } : o)));
                setPhotosOrder((p) => (p ? { ...p, completion_photos: next } : p));
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!partsOrder} onOpenChange={(open) => { if (!open) setPartsOrder(null); }}>
        <DialogContent className="max-w-2xl" data-testid="order-parts-dialog">
          <DialogHeader>
            <DialogTitle>Parts used · {partsOrder?.customer_name}</DialogTitle>
            <DialogDescription>
              Adding a part here decrements stock immediately — use this when parts are actually consumed on the job. The line is added to the invoice total so the customer pays for it.
            </DialogDescription>
          </DialogHeader>

          {partsOrder && (
            <div className="space-y-4">
              {/* Existing inventory lines */}
              {(partsOrder.items || []).some((it) => it.product_id) && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Already on this invoice</div>
                  <ul className="mt-2 space-y-1">
                    {(partsOrder.items || []).filter((it) => it.product_id).map((it) => (
                      <li key={it.line_id || `${it.product_id}-${it.name}`} className="flex items-center justify-between gap-3 rounded-md bg-white px-3 py-2 text-sm">
                        <div className="min-w-0">
                          <div className="truncate font-medium text-slate-900">{it.quantity}× {it.name}</div>
                          <div className="text-xs text-slate-500">
                            {fmt(it.price_cents * it.quantity)}
                            {it.added_by_name ? <> · added by {it.added_by_name}</> : null}
                          </div>
                        </div>
                        {it.line_id && (
                          <button
                            type="button"
                            onClick={() => removePart(it)}
                            disabled={removingLineId === it.line_id}
                            className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-white px-2.5 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                            data-testid={`order-parts-remove-${it.line_id}`}
                            title="Remove line and credit stock back"
                          >
                            {removingLineId === it.line_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                            Remove · credit stock
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Add new part */}
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
                <div className="text-xs font-semibold uppercase tracking-widest text-emerald-800">Add part from inventory</div>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1fr_110px_auto]">
                  <Select value={partsForm.product_id} onValueChange={(v) => setPartsForm({ ...partsForm, product_id: v })}>
                    <SelectTrigger data-testid="order-parts-product-select"><SelectValue placeholder="Select a product…" /></SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.id} disabled={p.stock <= 0}>
                          {p.name} · {fmt(p.price_cents)} · stock {p.stock}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min={1}
                    value={partsForm.quantity}
                    onChange={(e) => setPartsForm({ ...partsForm, quantity: e.target.value })}
                    data-testid="order-parts-quantity"
                  />
                  <Button onClick={addPart} disabled={addingPart || !partsForm.product_id} data-testid="order-parts-add-submit" className="gap-1">
                    {addingPart ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
                  </Button>
                </div>
                <p className="mt-2 text-[11px] text-emerald-800/80">New total will be {fmt(partsOrder?.total_cents || 0)}. Stock is decremented the moment you click Add.</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
